import React, { useState } from "react";
import { Controller } from "react-hook-form";
import {
  InputLabel,
  AppInputWrapper,
  ErrorLabel,
} from "./AppSelectDropdown.styles";
import styled from "styled-components";
import { ChevronDown, ChevronRight } from "lucide-react";
import { theme } from "@src/theme";

export interface AppSelectDropdownOption {
  [key: string]: any;
  value?: string;
  label?: string;
}

interface AppSelectDropdownProps {
  label: string;
  name: string;
  control?: any;
  errors: any;
  placeholder: string;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  successStyle?: boolean;
  options: AppSelectDropdownOption[];
  renderItem?: (
    option: AppSelectDropdownOption,
    isSelected: boolean,
    onSelect: () => void
  ) => React.ReactNode;
  labelAccessor?: string;
  valueAccessor?: string;
  onChange?: (value: string) => void;
  resetValue?: () => void;
  value?: string;
  selectContainerStyle?: React.CSSProperties;
  disabled?: boolean;
  setSearchText?: React.Dispatch<React.SetStateAction<string>>;
  searchText?: string;
  searchPlaceholder?: string;
  searching?: boolean;
  overrideBorderBottomColor?: string;
}

const HiddenSelect = styled.select`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

const CustomDropdown = styled.div<{
  $success?: boolean;
  $hasError?: boolean;
  disabled?: boolean;
  overrideBorderBottomColor?: string;
}>`
  position: relative;
  border-bottom: 1px solid
    ${({ theme, $success, $hasError, overrideBorderBottomColor }): string =>
      $hasError
        ? theme.colors.red
        : overrideBorderBottomColor
          ? overrideBorderBottomColor
          : $success
            ? theme.colors.green
            : theme.colors.yellow};
  padding: 8px 0px;
  width: 100%;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  z-index: 1;
`;

const DropdownMenu = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: ${({ theme }): string => theme.colors.darkBg};

  z-index: 10;
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 200px;
  overflow-y: auto;
`;

export const AppDropdownItem = styled.li`
  padding: 8px 12px;
  cursor: pointer;
  color: ${({ theme }): string => theme.colors.gray};
  &:hover {
    color: ${({ theme }): string => theme.colors.yellow};
  }
  &:focus: {
    border: 1px solid ${({ theme }): string => theme.colors.yellow};
  }
  user-select: none;
`;

const AppSelectDropdown = React.forwardRef<
  HTMLDivElement,
  AppSelectDropdownProps
>(
  (
    {
      label,
      name,
      control,
      errors,
      placeholder,
      icon,
      iconRight,
      successStyle,
      options,
      labelAccessor = "label",
      valueAccessor = "value",
      renderItem,
      onChange,
      value,
      selectContainerStyle = {},
      disabled,
      setSearchText,
      searchText,
      searchPlaceholder,
      searching,
      resetValue,
      overrideBorderBottomColor,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setIsOpen(false);
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(false);
        }}
        ref={ref}
        style={{
          marginBottom: "1rem",
          width: "100%",
          minHeight: 100,
          ...selectContainerStyle,
        }}
      >
        <InputLabel htmlFor={name}>{label}</InputLabel>
        <AppInputWrapper
          $success={successStyle}
          $leftIcon={!!icon}
          $iconRight={!!iconRight}
          overrideBorderBottomColor={overrideBorderBottomColor}
        >
          {icon && <span className="input-icon">{icon}</span>}
          <span className="input-icon-right">
            <span
              style={{
                display: "flex",
                alignItems: "center",
                position: "relative",
              }}
            >
              {iconRight && <>{iconRight}</>}
              {value && resetValue && (
                <span
                  style={{
                    cursor: "pointer",
                    zIndex: 15,
                    marginRight: 8,
                    position: "relative",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(false);
                    resetValue && resetValue();
                  }}
                >
                  x
                </span>
              )}
              {isOpen ? (
                <ChevronDown
                  className="chevron-icon"
                  color={theme.colors.gray}
                  size={20}
                />
              ) : (
                <ChevronRight
                  className="chevron-icon"
                  color={theme.colors.gray}
                  fontWeight={600}
                  size={20}
                />
              )}
            </span>
          </span>
          <Controller
            disabled={disabled}
            name={name}
            control={control}
            render={({ field }): React.JSX.Element => {
              const hasError = errors && !!errors[name];
              return (
                <>
                  <HiddenSelect
                    disabled={disabled}
                    {...field}
                    id={name}
                    value={field.value || ""}
                    onChange={(e): void => {
                      e.stopPropagation();
                      onChange && onChange(e.target.value);
                      field.onChange(e.target.value);
                      setIsOpen(false);
                    }}
                  >
                    <option value="">{placeholder}</option>
                    {options.map((option) => (
                      <option
                        key={(option as AppSelectDropdownOption)[valueAccessor]}
                        value={
                          (option as AppSelectDropdownOption)[valueAccessor]
                        }
                      >
                        {(option as AppSelectDropdownOption)[labelAccessor]}
                      </option>
                    ))}
                  </HiddenSelect>
                  <CustomDropdown
                    disabled={disabled}
                    $success={successStyle}
                    $hasError={hasError}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (disabled) return;
                      // Don't toggle dropdown if clicking on iconRight area (but allow chevron clicks)
                      const target = e.target as HTMLElement;
                      const clickedInIconRight =
                        target.closest(".input-icon-right");
                      const clickedChevron = target.closest(".chevron-icon");

                      if (clickedInIconRight && !clickedChevron) {
                        return;
                      }

                      setIsOpen((prev) => !prev);
                    }}
                    overrideBorderBottomColor={overrideBorderBottomColor}
                  >
                    {isOpen && setSearchText ? (
                      <input
                        className={errors[name] ? "error" : ""}
                        value={searchText || ""}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={searchPlaceholder || "Search"}
                        autoFocus
                      />
                    ) : (
                      <span
                        style={{
                          opacity: value || field.value ? 1 : 0.5,
                        }}
                      >
                        {options.find(
                          (opt) =>
                            (opt as AppSelectDropdownOption)[valueAccessor] ===
                            field.value
                        )?.[labelAccessor] || placeholder}
                      </span>
                    )}
                  </CustomDropdown>
                  {isOpen && (
                    <DropdownMenu>
                      {options.map((option) =>
                        renderItem ? (
                          <div
                            key={
                              (option as AppSelectDropdownOption)[
                                valueAccessor as keyof AppSelectDropdownOption
                              ]
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              onChange &&
                                onChange(
                                  (option as AppSelectDropdownOption)[
                                    valueAccessor as keyof AppSelectDropdownOption
                                  ]
                                );
                              field.onChange(
                                (option as AppSelectDropdownOption)[
                                  valueAccessor as keyof AppSelectDropdownOption
                                ]
                              );
                              setIsOpen(false);
                            }}
                          >
                            {renderItem(
                              option,
                              (option as AppSelectDropdownOption)[
                                valueAccessor
                              ] === field.value,
                              () => {
                                onChange &&
                                  onChange(
                                    (option as AppSelectDropdownOption)[
                                      valueAccessor as keyof AppSelectDropdownOption
                                    ]
                                  );
                                field.onChange(
                                  (option as AppSelectDropdownOption)[
                                    valueAccessor as keyof AppSelectDropdownOption
                                  ]
                                );
                                setIsOpen(false);
                              }
                            )}
                          </div>
                        ) : (
                          <AppDropdownItem
                            key={
                              (option as AppSelectDropdownOption)[
                                valueAccessor as keyof AppSelectDropdownOption
                              ]
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              onChange &&
                                onChange(
                                  (option as AppSelectDropdownOption)[
                                    valueAccessor as keyof AppSelectDropdownOption
                                  ]
                                );
                              field.onChange(
                                (option as AppSelectDropdownOption)[
                                  valueAccessor as keyof AppSelectDropdownOption
                                ]
                              );
                              setIsOpen(false);
                            }}
                          >
                            {(option as any)[labelAccessor]}
                          </AppDropdownItem>
                        )
                      )}
                      {searching ? (
                        <AppDropdownItem
                          onClick={() => {
                            setIsOpen(false);
                          }}
                          style={{ opacity: 0.5, color: "white" }}
                        >
                          Loading...
                        </AppDropdownItem>
                      ) : (
                        options.length === 0 && (
                          <AppDropdownItem
                            onClick={() => {
                              setIsOpen(false);
                            }}
                            style={{ opacity: 0.5, color: "white" }}
                          >
                            No options available
                          </AppDropdownItem>
                        )
                      )}
                    </DropdownMenu>
                  )}
                </>
              );
            }}
          />
        </AppInputWrapper>
        {errors[name] && (
          <ErrorLabel className="error-message">
            {errors[name]?.message}
          </ErrorLabel>
        )}
      </div>
    );
  }
);

export default AppSelectDropdown;
